import React from "react";
import {
  Box,
  Container,
  Stack,
  Typography,
  Grid,
  Skeleton,
} from "@mui/material";
import { useGetInvestmentPortfolioNpsQuery } from "../../store/api/finfactor.apislice";
import AppCardComponent from "../../components/mui/AppCardComponent";
import { isNative } from "../../utils/platform.utils";

import CircleIcon from "../../assets/circleicon.svg";

import NodisplayComponent from "../../components/common/NodisplayComponent";

export const NpsAccountRow = ({
  title,
  subtitle,
  primaryValue,
  primaryLabel,
  metrics = [],
}) => {
  return (
    <Box
      sx={{
        backgroundColor: "rgba(255, 255, 255, 0.03)",
        borderRadius: "16px",
        p: { xs: 1.5, md: 2 },
        mb: 2,
        border: "1px solid rgba(255, 255, 255, 0.05)",
      }}
    >
      <Grid container alignItems="center">
        {/* Left Section: Scheme Name and Preference */}
        <Grid item xs={12} md={5}>
          <Box>
            <Typography
              variant="body1"
              fontWeight="600"
              color="white"
              sx={{
                fontSize: { xs: "0.95rem", md: "1rem" },
              }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography
                variant="caption"
                sx={{ color: "rgba(255, 255, 255, 0.5)" }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        </Grid>

        {/* Middle Section: Current Amount */}
        <Grid item xs={6} md={3}>
          <Box sx={{ textAlign: { xs: "left", md: "right" }, mt: { xs: 2, md: 0 }, pr: { md: 4 } }}>
            {primaryLabel && (
              <Typography
                variant="caption"
                display="block"
                sx={{ color: "rgba(255, 255, 255, 0.5)" }}
              >
                {primaryLabel}
              </Typography>
            )}
            <Typography
              variant="body1"
              fontWeight="700"
              color="#38bdf8"
              sx={{ fontSize: { xs: "0.95rem", md: "1.1rem" } }}
            >
              {primaryValue}
            </Typography>
          </Box>
        </Grid>

        {/* Vertical Separator (Desktop only) */}
        <Grid item md={0.5} sx={{ display: { xs: "none", md: "flex" }, justifyContent: "center" }}>
          <Box
            sx={{
              height: 32,
              borderLeft: "1px solid rgba(255, 255, 255, 0.2)",
            }}
          />
        </Grid>

        {/* Right Section: Units Info */}
        <Grid item xs={6} md={3.5}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mt: { xs: 2, md: 0 },
              pl: { md: 1 },
              gap: 1
            }}
          >
            {metrics.map((metric, index) => (
              <Box key={index} sx={{ textAlign: "center", flex: 1 }}>
                <Typography
                  variant="caption"
                  display="block"
                  sx={{ color: "rgba(255, 255, 255, 0.5)" }}
                >
                  {metric.label}
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight="600"
                  color={metric.valueColor || "white"}
                >
                  {metric.value}
                </Typography>
              </Box>
            ))}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

const TierSection = ({ tierData, asOfDate }) => {
  if (!tierData) return null;

  return (
    <AppCardComponent
      sx={{
        bgcolor: "#13172a",
        borderRadius: "24px",
        border: "1px solid rgba(255, 255, 255, 0.05)",
        p: { xs: isNative() ? 1.25 : 1.5, md: 3 },
        mb: 4,
        "& .MuiCardHeader-root": { display: "none" },
        "& .MuiCardContent-root": {
          p: 0,
          "&:last-child": { pb: 0 },
        },
      }}
    >
      <Box sx={{ mb: 3 }}>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item xs={12} md={8}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                component="img"
                src={CircleIcon}
                sx={{
                  width: 28,
                  height: 28,
                  flexShrink: 0
                }}
              />
              <Stack direction={{ xs: "column", md: "row" }} spacing={{ xs: 0, md: 1 }} alignItems={{ xs: "flex-start", md: "baseline" }}>
                <Typography variant="h6" color="white" fontWeight="600" sx={{ fontSize: "1.15rem" }}>
                  {tierData.label}
                </Typography>
                <Typography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.4)", fontWeight: 400 }}>
                  {asOfDate}
                </Typography>
              </Stack>
            </Stack>
          </Grid>
          <Grid item xs={12} md={4} sx={{ textAlign: { xs: "left", md: "right" }, mt: { xs: 1, md: 0 } }}>
            <Typography variant="h5" color="white" fontWeight="700" sx={{ fontFamily: "'Outfit', sans-serif" }}>
              {tierData.totalValue}
            </Typography>
          </Grid>
        </Grid>
      </Box>

      {tierData.accounts.map((acc, idx) => (
        <NpsAccountRow
          key={idx}
          title={acc.scheme}
          subtitle={acc.schemePreference ? `Scheme Preference: ${acc.schemePreference}` : null}
          primaryValue={acc.currentAmount}
          primaryLabel="Current Amount"
          metrics={[
            { label: "Free Units", value: acc.freeUnits },
            { label: "Locked Units", value: acc.lockedUnits },
            { label: "Total Units", value: acc.totalUnits },
          ]}
        />
      ))}
    </AppCardComponent>
  );
};

const FinNpsDetailContainer = () => {
  const { data: npsData, isLoading, isError } = useGetInvestmentPortfolioNpsQuery();

  if (isLoading) {
    return (
      <Box sx={{ minHeight: "100vh", pb: 4 }}>
        <Container maxWidth="lg" sx={{ px: { xs: 1.5, sm: 2 }, mt: 2 }} disableGutters>
          <Skeleton
            variant="rectangular"
            height={200}
            sx={{ borderRadius: "24px", mb: 4, bgcolor: "rgba(255,255,255,0.05)" }}
          />
          <Skeleton
            variant="rectangular"
            height={200}
            sx={{ borderRadius: "24px", bgcolor: "rgba(255,255,255,0.05)" }}
          />
        </Container>
      </Box>
    );
  }

  if (isError || !npsData) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          pb: 4,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <NodisplayComponent
          title="No NPS Details Found"
          subtitle="We couldn't retrieve your NPS portfolio details at this time. Please try again later."
        />
      </Box>
    );
  }


  return (
    <Box sx={{ minHeight: "100vh", pb: 4, pt: 2 }}>
      <Container maxWidth="lg" sx={{ px: { xs: 1.5, sm: 2 } }} disableGutters>
        <TierSection tierData={npsData.tier1} asOfDate={npsData.asOfDate} />
        <TierSection tierData={npsData.tier2} asOfDate={npsData.asOfDate} />
      </Container>
    </Box>
  );
};

export default FinNpsDetailContainer;
